import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  arrayUnion,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  DetailShell,
  HCard,
  SerifHeading,
  HField,
  HPrimaryButton,
  HSecondaryButton,
  HButtonPair,
  HBadge,
} from '../h';
import { Switch } from '../ui/switch';
import { useHiglos } from './useHiglos';

/* v9 5.4 — Invite another family to a Higlo by email.

   Mirrors the direct-Firestore pattern from the pre-FEAT-133 webapp
   (HomeDashboard/IglooMembers.inviteFamilyToIgloo). No dedicated "invite"
   cloud function exists; the sender writes four docs itself and the invitee
   subscribes to the notifier collection on the next sign-in.

   Writes on successful invite (scoped to this Higlo):
     1. iglooInfo/{iglooID}/pending/{inviteeFamilyID}
          = { from: controllerCallerID, role, invitedAt: serverTimestamp() }
     2. iglooInfo/{iglooID} — arrayUnion inviteeFamilyID into .controllers
        (role==='controller') or .members (role==='member'). The role is
        baked in at invite time; post-accept promotion/demotion is done by
        mutating the same arrays.
     3. iglooInviteMessage/{inviteeFamilyID}/iglooIDs/{iglooID} = {}
        — the notifier doc the invitee's account subscribes to.
     4. familyInfo/{controllerCallerID}/connectedFamilies/{inviteeFamilyID}
          = { name: knownName }
        — the sender's private label for the invited family. Useful for
        rendering them as "The Patels" on the sender's dashboard even
        before the invitee's own family name is resolvable.

   Email → familyID resolution goes through /user/getPrimaryFamilyIDByEmail
   because the invitee's familyID isn't queryable from the client without
   index-level reads.

   Route: /app/higlos/:iglooID/invite
*/
export function InviteFamilyView({
  accountUUID,
  owningFamilies,
  primaryFamilyID,
  iglooID: iglooIDProp,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const iglooID = iglooIDProp || params.iglooID;
  const higlos = useHiglos({ owningFamilies, accountUUID, primaryFamilyID });
  const higlo = higlos[iglooID];

  const [email, setEmail] = useState('');
  const [knownName, setKnownName] = useState('');
  const [asController, setAsController] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const back = () => navigate(`/app/higlos/${iglooID}`);

  // Caller must be a controller to invite. Mirror the HigloDetail heuristic:
  // pick the first owned family that sits in iglooInfo.controllers.
  const controllers = higlo?.controllers || [];
  const owned = higlo?.ownedMemberships || [];
  const ownedInIgloo = owned.filter((m) => m.isMember !== false);
  // Whether the caller actually controls this Higlo. Kept separate from
  // controllerCallerID below, which falls back to primaryFamilyID and is
  // therefore always truthy — guarding on that let a member reach the writes.
  const isCallerController = ownedInIgloo.some((m) => controllers.includes(m.familyID));
  const controllerCallerID = ownedInIgloo
    .find((m) => controllers.includes(m.familyID))?.familyID
    || primaryFamilyID;

  const resolveEmailToFamilyID = async (rawEmail) => {
    const user = auth?.currentUser;
    if (!user) throw new Error('You need to be signed in to invite.');
    const idToken = await user.getIdToken();
    const res = await fetch(`${API_BASE_URL}/user/getPrimaryFamilyIDByEmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ email: rawEmail }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.primaryFamilyID) {
      throw new Error(
        data?.error || 'We couldn\u2019t find a Higlo account for that email.',
      );
    }
    return data.primaryFamilyID;
  };

  const submit = async () => {
    const trimmed = (email || '').trim();
    const trimmedName = (knownName || '').trim();
    if (!trimmed) {
      setError('Please enter their email.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setError('That doesn\u2019t look like an email address.');
      return;
    }
    if (!trimmedName) {
      setError('Give them a name so you can find them on your dashboard.');
      return;
    }
    if (!isCallerController) {
      setError('You need to be a Controller of this Higlo to invite another family.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const inviteeFamilyID = await resolveEmailToFamilyID(trimmed);

      // Refuse if the resolved family is already in this Higlo (own or other).
      const iglooRef = doc(db, 'iglooInfo', iglooID);
      const iglooSnap = await getDoc(iglooRef);
      if (!iglooSnap.exists()) {
        setError('This Higlo no longer exists.');
        setSubmitting(false);
        return;
      }
      const iglooData = iglooSnap.data();
      const existingControllers = iglooData.controllers || [];
      const existingMembers = iglooData.members || [];
      if (
        existingControllers.includes(inviteeFamilyID) ||
        existingMembers.includes(inviteeFamilyID)
      ) {
        setError('That family is already in this Higlo.');
        setSubmitting(false);
        return;
      }

      // Check for existing pending invite for this invitee (anti-double-invite).
      const pendingRef = doc(
        db,
        'iglooInfo',
        iglooID,
        'pending',
        inviteeFamilyID,
      );
      const pendingSnap = await getDoc(pendingRef);
      if (pendingSnap.exists()) {
        setError('You\u2019ve already invited them to this Higlo.');
        setSubmitting(false);
        return;
      }

      // 1. Pending doc (authority; invitee subscribes to it once they pick up
      //    the notifier below).
      await setDoc(pendingRef, {
        from: controllerCallerID,
        role: asController ? 'controller' : 'member',
        invitedAt: serverTimestamp(),
      });

      // 2. Optimistic add to iglooInfo arrays (arrayUnion matches the OLD
      //    IglooMembers.inviteFamilyToIgloo writes). Accept keeps the entry;
      //    decline arrayRemoves it back out.
      if (asController) {
        await updateDoc(iglooRef, {
          controllers: arrayUnion(inviteeFamilyID),
        });
      } else {
        await updateDoc(iglooRef, {
          members: arrayUnion(inviteeFamilyID),
        });
      }

      // 3. Notifier doc — the invitee's account listens to
      //    iglooInviteMessage/{myFID}/iglooIDs to discover pending invites.
      await setDoc(
        doc(
          db,
          'iglooInviteMessage',
          inviteeFamilyID,
          'iglooIDs',
          iglooID,
        ),
        {},
      );

      // 4. Record the sender's private label for this family in
      //    connectedFamilies. Matches the OLD webapp, which used this
      //    subcollection as the display-name source before the invitee's own
      //    familyInfo was readable.
      await setDoc(
        doc(
          db,
          'familyInfo',
          controllerCallerID,
          'connectedFamilies',
          inviteeFamilyID,
        ),
        { name: trimmedName },
        { merge: true },
      );

      back();
    } catch (err) {
      console.error('InviteFamilyView submit error', err);
      setError(err.message || 'Could not send the invite. Try again?');
    } finally {
      setSubmitting(false);
    }
  };

  // The route is directly navigable, so a member can reach it without going
  // through the (now controller-only) button on the Higlo detail screen. The
  // security rules would reject the invite, so say so rather than show a form
  // that cannot succeed. `higlo` is undefined on first render while the Higlo
  // loads — don't judge until it has arrived.
  if (higlo && !isCallerController) {
    return (
      <DetailShell
        onBack={back}
        breadcrumb={higlo?.name ? `${higlo.name} · invite` : 'Back to Higlo'}
        backIcon="close"
      >
        <div className="px-4 pb-6">
          <SerifHeading size="lg" className="mb-3 mt-3">
            Only Controllers can invite
          </SerifHeading>
          <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
            You&rsquo;re a member of this Higlo, so you can&rsquo;t invite other
            families to it. Ask one of its Controllers to send the invite.
          </p>
        </div>
      </DetailShell>
    );
  }

  return (
    <DetailShell
      onBack={back}
      breadcrumb={higlo?.name ? `${higlo.name} · invite` : 'Back to Higlo'}
      backIcon="close"
    >
      <div className="px-4 pb-6">
        <SerifHeading size="lg" className="mb-3 mt-3">
          Invite another family
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
          We&rsquo;ll send them an invite. Once they accept, their Hearth will
          appear in this Higlo.
        </p>

        <HField
          label="Their email"
          placeholder="name@example.com"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          className="mb-4"
        />

        <HField
          label="What you want to call them"
          placeholder="e.g. The Patels"
          value={knownName}
          onChange={(e) => setKnownName(e.target.value)}
          helpText="What you'll see them as on your dashboard. They won't see this name."
          className="mb-4"
        />

        <HCard className="p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Switch
                checked={asController}
                onCheckedChange={setAsController}
                aria-label="Make them a Controller"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-slate-900 mb-0.5">
                Make them a{' '}
                <HBadge variant="controller" className="align-middle ml-0.5">
                  Controller
                </HBadge>
              </div>
              <div className="text-[12px] text-slate-500 leading-relaxed">
                Controllers of this Higlo can invite more families, change
                positions, and rename it. Leave off if they should only be a
                member. You can always promote them later.
              </div>
            </div>
          </div>
        </HCard>

        {error && (
          <p className="text-[12px] text-red-600 mb-3 leading-relaxed">{error}</p>
        )}

        <HButtonPair>
          <HSecondaryButton onClick={back} disabled={submitting}>
            Cancel
          </HSecondaryButton>
          <HPrimaryButton onClick={submit} disabled={submitting}>
            {submitting ? 'Sending\u2026' : 'Send invitation'}
          </HPrimaryButton>
        </HButtonPair>
      </div>
    </DetailShell>
  );
}
